import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { values, get, pick } from 'lodash';
import {
  ControlLabel, FormControl,
  FormGroup, Button, Form, Modal, Checkbox,
  InputGroup, HelpBlock, Well,
} from 'react-bootstrap';
import { bindActionCreators } from 'redux';
import s from '../../../redux/selectors';
import a from '../../../redux/actions';
import types from '../../../types';
import { wrap, withT } from '../../../lib/wrappers';

const SocialLink = ({ value, icon, placeholder, socialLinkName, patch, error }) => (
  <FormGroup
    validationState={error ? 'error' : null}
  >
    <InputGroup>
      <InputGroup.Addon>
        <i className={`fa fa-${icon}`} />
      </InputGroup.Addon>
      <FormControl
        className={`account_form__social_link__${socialLinkName}`}
        placeholder={placeholder}
        type="text"
        value={value}
        onChange={e =>
          patch({ accountForm: { socialLinks: { [socialLinkName]: e.target.value } } })
        }
      />
    </InputGroup>
    {error && <HelpBlock>{error}</HelpBlock>}
  </FormGroup>
);
SocialLink.propTypes = {
  value: PropTypes.string,
  socialLinkName: PropTypes.string.isRequired,
  placeholder: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  patch: PropTypes.func.isRequired,
  error: PropTypes.string,
};

const Controls = ({ t, formValid, toggleModal, resetAccountForm, logout }) => (
  <FormGroup style={{ display: 'flex', justifyContent: 'space-around' }}>
    <Button
      disabled={!formValid}
      bsSize={'small'}
      onClick={() => toggleModal('saveUser')}
      className="account_form__save"
    >{t('Save')}</Button>
    <Button
      bsSize={'small'}
      onClick={resetAccountForm}
      className="account_form__reset"
    >{t('Reset Form')}</Button>
    <Button
      bsSize={'small'}
      onClick={logout}
      className="account_form__logout"
    >{t('Logout')}</Button>
    <Button
      bsSize={'small'}
      bsStyle="danger"
      onClick={() => toggleModal('deleteUser')}
      className="account_form__delete"
    >
      {t('Delete Account')}
    </Button>
  </FormGroup>
);
Controls.propTypes = {
  t: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  resetAccountForm: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  formValid: PropTypes.bool.isRequired,
};

const AccountForm = ({
  t, accountForm, updateUser, patch, modalDeleteUser, toggleModal,
  deleteUser, modalSaveUser, logout, resetAccountForm, password,
  accountFormValidations, accountFormErrors, setProfilePhoto,
  user, widgetExists, widgetWindow,
}) => {
  const modalStyle = {};
  if (widgetExists) modalStyle.top = widgetWindow.scrollY;
  return (
    <Form
      id="account_form"
    >
      <Controls
        t={t}
        toggleModal={toggleModal}
        resetAccountForm={resetAccountForm}
        logout={logout}
        formValid={values(accountFormValidations).every(v => v)}
      />

      <Well
        style={{ display: 'flex', justifyContent: 'center', flexDirection: 'column', alignItems: 'center' }}
      >
        {(accountForm.profilePhotoPreview || user.profilePhoto) &&
          <img
            style={{ maxHeight: '150px', padding: '10px', cursor: 'pointer' }}
            onClick={() => document.getElementById('profile_photo_input').click()}
            alt={t('Profile photo')}
            src={accountForm.profilePhotoPreview || user.profilePhoto}
          />
        }
        {!(accountForm.profilePhotoPreview || user.profilePhoto) &&
          <img
            style={{ height: '80px', padding: '10px', cursor: 'pointer' }}
            onClick={() => document.getElementById('profile_photo_input').click()}
            alt={t('Profile photo')}
            src={'/assets/img/account-active.png'}
          />
        }
        <FormControl
          id="profile_photo_input"
          style={{ opacity: '0', height: '1px', display: 'block' }}
          type="file"
          accept="image/x-png,image/jpeg"
          onChange={e => setProfilePhoto(e.target)}
        />
      </Well>
      <FormGroup
        validationState={(accountFormErrors.errEmailNonUnique || accountFormErrors.email) ? 'error' : (accountFormValidations.email ? 'success' : 'warning')}
      >
        <ControlLabel>{t('Email')}</ControlLabel>
        <FormControl
          id="account_form__email"
          componentClass="input"
          type="text"
          value={accountForm.email}
          onChange={e => patch({ accountForm: { email: e.target.value } })}
        />
        {accountFormErrors.errEmailNonUnique && <HelpBlock>{t('A user with this email already exists.')}</HelpBlock>}
        {accountFormErrors.email && <HelpBlock>{t('Must be a valid email address.')}</HelpBlock>}
      </FormGroup>
      <FormGroup
        validationState={accountFormValidations.firstName ? 'success' : null}
      >
        <ControlLabel>{t('First name')}</ControlLabel>
        <FormControl
          id="account_form__first_name"
          componentClass="input"
          type="text"
          value={accountForm.firstName}
          onChange={e => patch({ accountForm: { firstName: e.target.value } })}
        />
      </FormGroup>
      <FormGroup>
        <ControlLabel>{t('Last name')}</ControlLabel>
        <FormControl
          id="account_form__last_name"
          componentClass="input"
          type="text"
          value={accountForm.lastName}
          onChange={e => patch({ accountForm: { lastName: e.target.value } })}
        />
      </FormGroup>
      <FormGroup>
        <Checkbox
          id="account_form__public"
          checked={accountForm.public}
          onChange={e => patch({ accountForm: { public: e.target.checked } })}
        >{t('Share my footprint and pledges publicly')}</Checkbox>
      </FormGroup>

      <h3>{t('Tell us about yourself')}</h3>
      <FormGroup>
        <FormControl
          id="account_form__description"
          min={2000}
          componentClass="textarea"
          value={accountForm.descriptionMD}
          onChange={e => patch({ accountForm: { descriptionMD: e.target.value.slice(0, 2000) } })}
        />
        <HelpBlock>{accountForm.descriptionMD.length} / 2000</HelpBlock>
      </FormGroup>
      <SocialLink
        patch={patch}
        icon={'link'}
        placeholder={'Personal website'}
        socialLinkName={'website'}
        error={accountFormErrors.website ? t('Most be a valid URL.') : ''}
        value={get(accountForm, 'socialLinks.website')}
      />
      <SocialLink
        patch={patch}
        icon={'medium'}
        placeholder={'Medium username'}
        socialLinkName={'medium'}
        value={get(accountForm, 'socialLinks.medium')}
      />
      <SocialLink
        patch={patch}
        icon={'facebook-f'}
        placeholder={'Facebook profile link'}
        socialLinkName={'facebook'}
        value={get(accountForm, 'socialLinks.facebook')}
        error={accountFormErrors.facebook ? t('Most be a valid URL.') : ''}
      />
      <SocialLink
        patch={patch}
        icon={'twitter'}
        placeholder={'Twitter username'}
        socialLinkName={'twitter'}
        value={get(accountForm, 'socialLinks.twitter')}
      />
      <SocialLink
        patch={patch}
        icon={'linkedin'}
        placeholder={'Linkedin profile link'}
        socialLinkName={'linkedin'}
        value={get(accountForm, 'socialLinks.linkedin')}
        error={accountFormErrors.linkedin ? t('Most be a valid URL.') : ''}
      />
      <SocialLink
        patch={patch}
        icon={'instagram'}
        placeholder={'Instagram username'}
        socialLinkName={'instagram'}
        value={get(accountForm, 'socialLinks.instagram')}
      />

      <Controls
        t={t}
        toggleModal={toggleModal}
        resetAccountForm={resetAccountForm}
        logout={logout}
        formValid={values(accountFormValidations).every(v => v)}
      />

      <Modal
        id="account_form__delete_account_modal"
        style={modalStyle}
        show={modalDeleteUser}
        onHide={() => toggleModal('deleteUser')}
        backdrop
      >
        <Modal.Header>
          <Modal.Title>{t('Confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('By clicking below, you confirm that you want us to delete all of your data. It will not be recoverable.')}</Modal.Body>
        <Modal.Footer>
          <Button
            id="account_form__delete___cancel"
            onClick={() => toggleModal('deleteUser')}
          >{t('Cancel')}</Button>
          <Button
            id="account_form__delete___confirm"
            bsStyle="danger"
            onClick={deleteUser}
          >{t('Yes, please delete my account')}</Button>
        </Modal.Footer>
      </Modal>

      <Modal
        id="account_form__update_account_modal"
        style={modalStyle}
        show={modalSaveUser}
        onHide={() => toggleModal('saveUser')}
        backdrop
      >
        <Modal.Header>
          <Modal.Title>{t('Confirmation')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormGroup
            validationState={accountFormErrors.errPasswordIncorrect ? 'error' : null}
          >
            <ControlLabel>{t('Please enter your password to save changes')}</ControlLabel>
            <FormControl
              id="account_form__save_user_modal__password"
              componentClass="input"
              type="password"
              value={password}
              onChange={e => patch({ auth: { password: e.target.value } })}
            />
            {accountFormErrors.errPasswordIncorrect && <HelpBlock>{t('Password is incorrect.')}</HelpBlock>}
          </FormGroup>
        </Modal.Body>
        <Modal.Footer>
          <Button
            id="account_form__save_user_modal__confirm"
            onClick={() => updateUser(t)}
          >{t('Save')}</Button>
          <Button
            id="account_form__save_user_modal__cancel"
            onClick={() => toggleModal('saveUser')}
          >{t('Cancel')}</Button>
        </Modal.Footer>
      </Modal>
    </Form>
  );
};

AccountForm.propTypes = {
  t: PropTypes.func.isRequired,
  updateUser: PropTypes.func.isRequired,
  patch: PropTypes.func.isRequired,
  deleteUser: PropTypes.func.isRequired,
  toggleModal: PropTypes.func.isRequired,
  resetAccountForm: PropTypes.func.isRequired,
  setProfilePhoto: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  accountForm: types.User.isRequired,
  user: types.User.isRequired,
  modalDeleteUser: PropTypes.bool.isRequired,
  modalSaveUser: PropTypes.bool.isRequired,
  password: PropTypes.string,
  accountFormValidations: types.AccountFormValidations.isRequired,
  accountFormErrors: types.AccountFormErrors.isRequired,
  widgetExists: PropTypes.bool.isRequired,
  widgetWindow: types.WidgetWindow,
};

const mapStateToProps = (state, ownProps) => [
  'accountForm',
  'modalDeleteUser',
  'modalSaveUser',
  'password',
  'accountFormValidations',
  'user',
  'accountFormErrors',
  'widgetExists',
  'widgetWindow',
].reduce((props, selector) => ({
  ...props,
  [selector]: s[selector](state, ownProps),
}), {});


const mapDispatchToProps = dispatch => bindActionCreators(
  pick(a, [
    'updateUser',
    'patch',
    'toggleModal',
    'deleteUser',
    'resetAccountForm',
    'logout',
    'setProfilePhoto',
  ]),
  dispatch,
);

export default wrap(
  withT('auth'),
  connect(mapStateToProps, mapDispatchToProps)(AccountForm),
);
